/**
 * A six-digit code, one cell per digit (design: Carnet). The twin of Operator V2's
 * `ui/code-input.tsx`, kept in this package because the portal has its own stylesheet.
 *
 * One real `<input>` lies invisibly over the cells, so the browser keeps doing what it does
 * for a plain field: the label, `one-time-code` autofill from Mail, paste, the screen reader.
 * The cells are paint only (`aria-hidden`). A collapsed caret always sits at the end, so typing
 * fills left to right and Backspace empties right to left; a selection is kept, so a wrong
 * code can be replaced with one paste; a pasted « 123 456 » keeps its digits.
 *
 * The active cell carries the 2 px inset ink outline every Carnet selection uses, never a
 * tint; a wrong code turns the cells light red with red digits. `onComplete` fires once when
 * the last digit lands, so the form can send itself; the button stays.
 */
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type Ref,
  type SyntheticEvent,
} from 'react';

export function digitsOnly(raw: string, length: number): string {
  return raw.replace(/\D/gu, '').slice(0, length);
}

export function CodeInput({
  id,
  name,
  value,
  onChange,
  onComplete,
  length = 6,
  invalid = false,
  autoFocus = false,
  ref,
}: {
  id: string;
  name?: string | undefined;
  value: string;
  onChange: (value: string) => void;
  /** The last digit has landed (by typing, paste or autofill). */
  onComplete?: ((value: string) => void) | undefined;
  length?: number | undefined;
  /** The last code was refused: the cells say so until the next digit. */
  invalid?: boolean | undefined;
  autoFocus?: boolean | undefined;
  ref?: Ref<HTMLInputElement> | undefined;
}) {
  const [focused, setFocused] = useState(false);
  const typed = useRef(false);
  const completed = useRef<string | null>(null);

  // Completion is announced after the commit, not inside the change handler: by then the
  // input's own value is the clean six digits, so a form that sends itself passes its
  // `pattern` check. Once per distinct code the person typed; never for a code put back.
  useEffect(() => {
    if (value.length < length) {
      completed.current = null;
      return;
    }
    if (!typed.current || completed.current === value) return;
    typed.current = false;
    completed.current = value;
    onComplete?.(value);
  }, [value, length, onComplete]);

  const change = (event: ChangeEvent<HTMLInputElement>) => {
    typed.current = true;
    onChange(digitsOnly(event.target.value, length));
  };

  // A collapsed caret always sits after the last digit. A real selection (Ctrl/Cmd+A, a
  // shift-arrow, a double tap) is kept, so a refused code can be replaced by a paste or
  // deleted in one go instead of being backspaced through.
  const toEnd = (event: SyntheticEvent<HTMLInputElement>) => {
    const el = event.currentTarget;
    if (el.selectionStart !== el.selectionEnd) return;
    const end = el.value.length;
    if (el.selectionStart !== end) el.setSelectionRange(end, end);
  };

  const key = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.shiftKey || event.metaKey || event.ctrlKey) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Home') {
      event.preventDefault();
    }
  };

  const active = focused ? Math.min(value.length, length - 1) : -1;
  const cells = Array.from({ length }, (_, i) => value[i] ?? '');

  return (
    <div className="code" data-invalid={invalid || undefined}>
      <input
        ref={ref}
        id={id}
        name={name}
        className="code-input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern={`[0-9]{${length}}`}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        required
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        value={value}
        onChange={change}
        onKeyDown={key}
        onSelect={toEnd}
        onFocus={(event) => {
          setFocused(true);
          toEnd(event);
        }}
        onBlur={() => setFocused(false)}
      />
      <div className="code-cells" aria-hidden="true">
        {cells.map((digit, i) => (
          <span
            key={i}
            className="code-cell"
            data-active={i === active || undefined}
            data-filled={digit !== '' || undefined}
          >
            {digit !== '' ? (
              <span className="code-digit" key={`${i}-${digit}`}>
                {digit}
              </span>
            ) : null}
          </span>
        ))}
      </div>
    </div>
  );
}
